import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { GlobalEntityAutocomplete, EntityOption } from './GlobalEntityAutocomplete';
import { FunctionalSpecification } from '../types';

interface FSAutoCompleteProps {
  projectId: string;
  label: string;
  placeholder?: string;
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  currentEntityId?: string; // ID of the entity being edited (e.g., URS-002)
  currentEntityType?: 'URS' | 'TEST' | 'RISK' | 'SOP'; // Type of entity being edited
}

export const FSAutoComplete: React.FC<FSAutoCompleteProps> = ({
  projectId,
  label,
  placeholder,
  selectedIds,
  onChange,
  currentEntityId,
  currentEntityType
}) => {
  const [options, setOptions] = useState<EntityOption[]>([]);
  const [readOnlyIds, setReadOnlyIds] = useState<string[]>([]);

  useEffect(() => {
    const loadFS = async () => {
      if (!projectId) {
        return;
      }

      try {
        const col = collection(db, 'projects', projectId, 'fs');
        const snapshot = await getDocs(col);
        const loaded: EntityOption[] = snapshot.docs.map(doc => {
          const data = doc.data() as FunctionalSpecification;
          return {
            id: data.id || `FS-${doc.id.substring(0, 8)}`,
            label: data.title || data.id || `FS-${doc.id.substring(0, 8)}`,
            title: data.title
          };
        });
        setOptions(loaded);
      } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error loading FS for autocomplete:', err);
      }
        setOptions([]);
      }
    };

    loadFS();
  }, [projectId]);

  // Query for reverse relationships
  // When editing a URS/TEST/RISK/SOP, find FS that have this entity in their links
  useEffect(() => {
    const queryReverseLinks = async () => {
      if (!projectId || !currentEntityId || !currentEntityType) {
        setReadOnlyIds([]);
        return;
      }

      try {
        const reverseIds: string[] = [];
        
        // Query all FS documents to find which ones reference the current entity
        const fsCol = collection(db, 'projects', projectId, 'fs');
        const fsSnapshot = await getDocs(fsCol);
        fsSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const links = data.links || {};
          
          // Check if this FS references the current entity
          if (currentEntityType === 'URS' && links.urs_ids?.includes(currentEntityId)) {
            const id = data.id || `FS-${doc.id.substring(0, 8)}`;
            reverseIds.push(id);
          } else if (currentEntityType === 'TEST' && links.test_ids?.includes(currentEntityId)) {
            const id = data.id || `FS-${doc.id.substring(0, 8)}`;
            reverseIds.push(id);
          } else if (currentEntityType === 'RISK' && links.risk_ids?.includes(currentEntityId)) {
            const id = data.id || `FS-${doc.id.substring(0, 8)}`;
            reverseIds.push(id);
          } else if (currentEntityType === 'SOP' && links.sop_ids?.includes(currentEntityId)) {
            const id = data.id || `FS-${doc.id.substring(0, 8)}`;
            reverseIds.push(id);
          }
        });

        setReadOnlyIds(reverseIds);
      } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error querying reverse links for FS:', err);
      }
        setReadOnlyIds([]);
      }
    };

    queryReverseLinks();
  }, [projectId, currentEntityId, currentEntityType]);

  return (
    <GlobalEntityAutocomplete
      label={label}
      placeholder={placeholder}
      selectedIds={selectedIds}
      options={options}
      onChange={onChange}
      readOnlyIds={readOnlyIds}
    />
  );
};

